import { useState } from 'react'
import { App } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import productService from '../services/products.service'
import type { Product } from '../types/product.types'

export const useProductActions = () => {
    const { message, modal } = App.useApp()
    const queryClient = useQueryClient()
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingProduct, setEditingProduct] = useState<Product | null>(null)

    // Mutations
    const createMutation = useMutation({
        mutationFn: productService.createProduct,
        onSuccess: () => {
            message.success('Product added successfully!')
            queryClient.invalidateQueries({ queryKey: ['products'] })
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error('Failed to add product: ' + (error.response?.data?.message || error.message))
        }
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => productService.updateProduct(id, data),
        onSuccess: () => {
            message.success('Product updated successfully!')
            queryClient.invalidateQueries({ queryKey: ['products'] })
            setIsFormModalOpen(false)
            if (isDetailDrawerOpen && selectedProduct) {
                // Determine if we need to update selectedProduct details or just close drawer.
                // For simplicity, we might just re-fetch details or let user close.
                // Ideally, we'd refetch 'selectedProduct' too if it was query based.
                setIsDetailDrawerOpen(false)
            }
        },
        onError: (error: any) => {
            message.error('Failed to update product: ' + (error.response?.data?.message || error.message))
        }
    })

    const deleteMutation = useMutation({
        mutationFn: productService.deleteProduct,
        onSuccess: () => {
            message.success('Product deleted!')
            queryClient.invalidateQueries({ queryKey: ['products'] })
            setIsDetailDrawerOpen(false)
        },
        onError: () => {
            message.error('Failed to delete product')
        }
    })

    const handleViewDetails = (product: Product) => {
        setSelectedProduct(product)
        setIsDetailDrawerOpen(true)
    }

    const handleAddProduct = () => {
        setEditingProduct(null)
        setIsFormModalOpen(true)
    }

    const handleEditProduct = (product: Product) => {
        setEditingProduct(product)
        setIsFormModalOpen(true)
    }

    const handleDeleteProduct = (product: Product) => {
        modal.confirm({
            title: 'Delete Product',
            content: `Are you sure you want to delete ${product.name}?`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => {
                deleteMutation.mutate(product.id)
            },
        })
    }

    const handleFormSubmit = (values: any) => {
        if (editingProduct) {
            updateMutation.mutate({ id: editingProduct.id, data: values })
        } else {
            createMutation.mutate(values)
        }
    }

    const closeDrawer = () => setIsDetailDrawerOpen(false)
    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        selectedProduct,
        isDetailDrawerOpen,
        isFormModalOpen,
        editingProduct,
        handleViewDetails,
        handleAddProduct,
        handleEditProduct,
        handleDeleteProduct,
        handleFormSubmit,
        closeDrawer,
        closeFormModal,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending
    }
}
